import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';
import { Key, CheckCircle } from '@phosphor-icons/react';

const API = '/api';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Forgot password state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotMessage(data.message);
    } catch (err) {
      setForgotError(formatApiErrorDetail(err.response?.data?.detail) || 'Could not submit the request. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgot = () => {
    setForgotOpen(false);
    setForgotEmail('');
    setForgotError('');
    setForgotMessage('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <img src="/roottrace-logo-light.png" alt="RootTrace" className="w-72 h-auto object-contain mb-2" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full rounded-lg"
                data-testid="login-email"
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-slate-700">Password</label>
                <button
                  type="button"
                  onClick={() => { setForgotOpen(true); setForgotEmail(email); }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full rounded-lg"
                data-testid="login-password"
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg h-12 text-base font-medium"
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-600">
            Don't have an account?{' '}
            <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium">
              Register here
            </Link>
          </div>

        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onOpenChange={(open) => { if (!open) closeForgot(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Key size={22} weight="duotone" className="text-blue-600" />
              Reset Password
            </DialogTitle>
          </DialogHeader>

          {!forgotMessage ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <p className="text-sm text-slate-600">
                Enter your registered email address. If the account exists, a temporary password will be emailed to you.
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
                <Input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@organisation.gov"
                  className="rounded-lg"
                  data-testid="forgot-email"
                  required
                />
              </div>

              {forgotError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm" data-testid="forgot-error">
                  {forgotError}
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg"
                  data-testid="forgot-submit"
                >
                  {forgotLoading ? 'Sending...' : 'Send Temporary Password'}
                </Button>
                <Button type="button" variant="outline" onClick={closeForgot} className="rounded-lg">
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-4" data-testid="forgot-success">
              <div className="flex items-start gap-2 text-green-700 bg-green-50 border border-green-200 p-3 rounded-lg">
                <CheckCircle size={20} weight="fill" className="shrink-0 mt-0.5" />
                <span className="text-sm">{forgotMessage}</span>
              </div>
              <Button onClick={closeForgot} className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg" data-testid="forgot-done">
                Back to Sign In
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
